import bbox from '@turf/bbox';
import { point, featureCollection } from '@turf/helpers';
import { Attributes, DataSources } from '@leverege/ui-attributes';

/**
 * Create bounds using the Attribute 'geoPosition' 
 */
function getLatLonBounds(features, form = 'lonLatArray') {
  if (features.length > 0) {
    const [minLon, minLat, maxLon, maxLat] = bbox(featureCollection(features));
    if (form === 'lonLatArray') {
      return [[minLon, minLat], [maxLon, maxLat]];
    }
    return {
      minLat,
      maxLat,
      minLon,
      maxLon
    };
  }
  return null;
}
function getFeatureList(items) {
  const features = [];
  for (let n = 0; n < items.length; n++) {
    const obj = items[n];
    if (obj == null) {
      continue;
    }
    const ll = Attributes.get('geoPosition', obj.type, obj.data);
    if (ll) {
      features.push(point([ll.lon, ll.lat]));
    } else {
      const sf = getShapeFeatures(obj.type, obj.data);
      if (sf && sf.length > 0) {
        sf.forEach(f => {
          if (f && f.geometry) {
            features.push(f);
          }
        });
      }
    }
  }
  return features;
}
function getPosition(obj) {
  if (obj == null) {
    return null;
  }
  let data = obj.data;
  if (data == null) {
    data = DataSources.getData(obj);
  }
  if (data) {
    return Attributes.get('geoPosition', obj.type, data);
  }
  return null;
}

/**
 * Creates a bounds by combining all geoJson attributes into a featureCollection
 * and calculating its bounds
 */
function getShapeBounds(obj, form = 'lonLatArray') {
  if (obj == null) {
    return null;
  }
  let data = obj.data;
  if (data == null) {
    data = DataSources.getData(obj);
  }
  if (data) {
    const features = getShapeFeatures(obj.type, data);
    if (features.length > 0) {
      const [minLon, minLat, maxLon, maxLat] = bbox(featureCollection(features));
      if (form === 'lonLatArray') {
        return [[minLon, minLat], [maxLon, maxLat]];
      }
      return {
        minLat,
        maxLat,
        minLon,
        maxLon
      };
    }
  }
  return null;
}
function getShapeFeatures(objectType, data) {
  const geoJson = Attributes.getAttributesFor(objectType).filter(attr => attr.valueType === 'geoJson');
  const features = geoJson.map(attr => {
    const s = attr.get(data);
    if (typeof s === 'string') {
      try {
        return JSON.parse(s);
      } catch (e) {
        return null;
      }
    }
    return s;
  }).filter(s => s != null);
  return features;
}
function getBoundsOrPointFromBBox(bbox) {
  if (bbox == null) {
    return {};
  }
  if (Math.abs(bbox[0][0] - bbox[1][0]) < 0.000001 && Math.abs(bbox[0][0] - bbox[1][0]) < 0.000001) {
    // its a point. 
    return {
      point: {
        lat: bbox[0][1],
        lon: bbox[0][0]
      }
    };
  }
  return {
    bounds: bbox
  };
}
function getBoundsOrPointFromItems(items) {
  const features = getFeatureList(items);
  const llbox = getLatLonBounds(features);
  return getBoundsOrPointFromBBox(llbox);
}

/**
 * Will look at the Object Ref and extract maxBounds, minZoom and maxZoom
 * from its metadata. It will assign the values to state.maxBounds, state.minZoom or
 * state.maxZoom if state does not have them set already.
 */
function getConstraintsFromObjRef(objRef, state) {
  /* eslint-disable no-param-reassign */
  if (objRef?.data == null) {
    return;
  }
  const {
    metadata
  } = objRef.data;
  const {
    maxBounds,
    minZoom,
    maxZoom
  } = metadata || {};
  if (minZoom != null && state.minZoom == null) {
    state.minZoom = minZoom;
  }
  if (maxZoom != null && state.maxZoom == null) {
    state.maxZoom = maxZoom;
  }
  if (maxBounds && state.maxBounds == null) {
    state.maxBounds = maxBounds;
  }
  /* eslint-enable no-param-reassign */
}

/**
 * Will look at the Object Ref and extra position or bounds
 * from its metadata or geoPosition attribute. It will assign
 * the values to state.mapBounds or state.center. If state 
 * already has either mapBounds or center, this will do nothing.
 */
function getPositionFromObjRef(objRef, state) {
  /* eslint-disable no-param-reassign */
  if (objRef?.data == null) {
    return;
  }
  const positionSet = state.mapBounds != null || state.center != null;
  const {
    metadata,
    zoom
  } = objRef.data;
  const {
    mapBounds
  } = metadata || {};
  if (!positionSet) {
    if (mapBounds) {
      state.mapBounds = mapBounds;
      state.center = null;
    } else {
      const pos = Attributes.get('geoPosition', objRef.type, objRef.data);
      if (pos) {
        state.center = [pos.lon, pos.lat];
      }
      if (state.zoom == null && zoom != null) {
        state.zoom = zoom;
      }
    }
  }
  /* eslint-enable no-param-reassign */
}
export default {
  getBoundsOrPointFromItems,
  getBoundsOrPointFromBBox,
  getLatLonBounds,
  getPosition,
  getShapeBounds,
  getFeatureList,
  getConstraintsFromObjRef,
  getPositionFromObjRef
};